/**
 * @file apps/web/src/components/DefectCard.tsx
 * @description Card component for displaying a single defect with full lifecycle controls.
 * Shows severity, status, title, description, assignment, and resolution actions.
 */

import { useState } from 'react';
import { Bug, User, Clock, ChevronDown, ChevronUp, ExternalLink, FileText } from 'lucide-react';
import { Badge, SeverityBadge, StatusBadge } from './Badge.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Defect {
  id:                 string;
  defect_number:      string;
  title:              string;
  description:        string;
  severity:           string;
  status:             string;
  priority?:          string;
  screenshot_urls?:   string[];
  reported_by_name?:  string | null;
  assigned_to_name?:  string | null;
  resolution_notes?:  string | null;
  resolved_at?:       string | null;
  created_at:         string;
  session_id?:        string | null;
  test_script_id?:    string | null;
  // "Source Script" — the test script this defect was raised against, if
  // any. Null for defects logged directly (no script link).
  test_script_title?: string | null;
}

interface DefectCardProps {
  defect:        Defect;
  expanded?:     boolean;
  onStatusChange?: (id: string, status: string) => void;
  onAssign?:     (id: string) => void;
  className?:    string;
  currentProjectRole?: string;
}

// ---------------------------------------------------------------------------
// Status transition options
// ---------------------------------------------------------------------------

const STATUS_TRANSITIONS: Record<string, string[]> = {
  open:        ['in_progress', 'wont_fix'],
  in_progress: ['resolved', 'deferred', 'open'],
  resolved:    ['closed', 'open'],
  closed:      [],
  wont_fix:    ['open'],
  deferred:    ['open', 'in_progress'],
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// DefectCard
// ---------------------------------------------------------------------------

export function DefectCard({
  defect,
  expanded: defaultExpanded = false,
  onStatusChange,
  className = '',
  currentProjectRole,
}: DefectCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // 'closed' is server-enforced to project-lead-only (PATCH /uat/defects/:id)
  // — dropped from the offered buttons here too, so a non-lead user isn't
  // shown a button that will just 403.
  const transitions = (STATUS_TRANSITIONS[defect.status] ?? [])
    .filter((t) => t !== 'closed' || currentProjectRole === 'lead');

  return (
    <div className={['card', className].join(' ')}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Left: number + title */}
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="shrink-0 mt-0.5">
              <Bug size={15} className="text-muted" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono text-muted">{defect.defect_number}</span>
                <SeverityBadge severity={defect.severity} />
                <StatusBadge status={defect.status} />
              </div>
              <h3 className="text-sm font-semibold text-ink mt-0.5 leading-snug">
                {defect.title}
              </h3>
            </div>
          </div>

          {/* Right: expand toggle */}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="btn-icon shrink-0"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-4 mt-2 text-[11px] font-mono text-muted flex-wrap">
          <span className="flex items-center gap-1">
            <FileText size={10} />
            Source Script:{' '}
            <span className={defect.test_script_title ? 'text-ink font-medium' : ''}>
              {defect.test_script_title ?? '—'}
            </span>
          </span>
          {defect.reported_by_name && (
            <span className="flex items-center gap-1">
              <User size={10} />
              Reported by {defect.reported_by_name}
            </span>
          )}
          {defect.assigned_to_name && (
            <span className="flex items-center gap-1">
              <User size={10} />
              Assigned to{' '}
              <span className="text-ink font-medium">{defect.assigned_to_name}</span>
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock size={10} />
            {formatDate(defect.created_at)}
          </span>
        </div>
      </div>

      {/* Expandable body */}
      {expanded && (
        <div className="border-t border-[rgba(8,17,31,0.06)] px-4 pb-4 pt-3 space-y-3">
          {/* Description */}
          <p className="text-sm text-ink/80 leading-relaxed">{defect.description}</p>

          {/* Resolution notes */}
          {defect.resolution_notes && (
            <div className="bg-success/8 rounded p-3">
              <p className="text-[11px] font-mono font-semibold text-success uppercase tracking-wider mb-1">
                Resolution
              </p>
              <p className="text-sm text-ink/80">{defect.resolution_notes}</p>
              {defect.resolved_at && (
                <p className="text-[11px] font-mono text-muted mt-1">
                  Resolved {formatDate(defect.resolved_at)}
                </p>
              )}
            </div>
          )}

          {/* Screenshots */}
          {defect.screenshot_urls && defect.screenshot_urls.length > 0 && (
            <div>
              <p className="section-label mb-2">Screenshots</p>
              <div className="flex gap-2 flex-wrap">
                {defect.screenshot_urls.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-blue hover:underline font-mono"
                  >
                    <ExternalLink size={10} />
                    Screenshot {i + 1}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Status transition actions */}
          {transitions.length > 0 && onStatusChange && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <span className="text-[11px] font-mono text-muted">Move to:</span>
              {transitions.map((nextStatus) => (
                <button
                  key={nextStatus}
                  onClick={() => onStatusChange(defect.id, nextStatus)}
                  className="btn-ghost py-1 px-2.5 text-xs"
                >
                  {nextStatus.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DefectList — renders a list of DefectCards
// ---------------------------------------------------------------------------

interface DefectListProps {
  defects:        Defect[];
  loading?:       boolean;
  onStatusChange?: (id: string, status: string) => void;
  emptyMessage?:  string;
  currentProjectRole?: string;
}

export function DefectList({ defects, loading, onStatusChange, emptyMessage, currentProjectRole }: DefectListProps) {
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card h-20 bg-gray/60" />
        ))}
      </div>
    );
  }

  if (defects.length === 0) {
    return (
      <div className="empty-state">
        <Bug size={32} className="text-muted/40 mb-3" />
        <p className="text-sm text-muted">{emptyMessage ?? 'No defects found.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {defects.map((d) => (
        <DefectCard key={d.id} defect={d} onStatusChange={onStatusChange} currentProjectRole={currentProjectRole} />
      ))}
    </div>
  );
}
